import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatEventDate } from "../../lib/api";
import { Trash, EnvelopeSimple, Eye, Archive } from "@phosphor-icons/react";

const STATUS = ["new", "read", "archived"];
const TYPES = [
  { id: "", label: "All types" },
  { id: "general", label: "General" },
  { id: "sponsorship", label: "Sponsorship" },
  { id: "speaking", label: "Speaking" },
  { id: "press", label: "Press" },
  { id: "partnership", label: "Partnership" },
];

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState({ status: "new", type: "" });
  const [active, setActive] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (filter.status) params.contact_status = filter.status;
    if (filter.type) params.contact_type = filter.type;
    const { data } = await api.get("/admin/contacts", { params });
    setContacts(data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/contacts/${id}/status`, { status });
      setActive((a) => (a && a.id === id ? data : a));
      load();
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/contacts/${id}`);
    if (active?.id === id) setActive(null);
    load();
  };

  const counts = useMemo(() => contacts.reduce((acc, c) => { acc[c.type] = (acc[c.type] || 0) + 1; return acc; }, {}), [contacts]);

  return (
    <div data-testid="admin-contacts-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="term-label">/ inbox</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">Contact submissions.</h1>
        </div>
        <div className="flex items-center gap-2 cyber">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-3 py-2" data-testid="filter-contact-status">
            <option value="">All statuses</option>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="px-3 py-2" data-testid="filter-contact-type">
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        <div className="xl:col-span-7 hard-border bg-cyber-surface">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-cyber-line bg-cyber-elevated font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/60">
            <div className="col-span-4">From</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-3">When</div>
            <div className="col-span-3 text-right">Action</div>
          </div>
          {contacts.length === 0 && <div className="p-8 font-mono-tech text-white/50">/ no submissions match this filter.</div>}
          {contacts.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => { setActive(c); if (c.status === "new") setStatus(c.id, "read"); }}
              onKeyDown={(e) => { if (e.key === "Enter") { setActive(c); if (c.status === "new") setStatus(c.id, "read"); }}}
              className={`grid grid-cols-12 gap-2 px-5 py-4 border-b border-cyber-line items-center hover:bg-cyber-elevated cursor-pointer ${active?.id === c.id ? "bg-cyber-elevated" : ""}`}
              data-testid={`contact-row-${c.id}`}
            >
              <div className="col-span-4">
                <div className="text-white flex items-center gap-2">
                  {c.status === "new" && <span className="w-2 h-2 bg-cyber-green inline-block" data-testid="unread-dot" />}
                  {c.name}
                </div>
                <div className="font-mono-tech text-xs text-white/50">{c.email}</div>
              </div>
              <div className="col-span-2"><TypePill type={c.type} /></div>
              <div className="col-span-3 font-mono-tech text-xs text-white/60">{formatEventDate(c.created_at)} · {new Date(c.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                <button onClick={(e) => { e.stopPropagation(); setStatus(c.id, "archived"); }} disabled={busyId === c.id} className="text-white/60 hover:text-white" data-testid={`archive-${c.id}`}><Archive size={14} /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="text-cyber-red hover:text-white" data-testid={`del-${c.id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="xl:col-span-5 hard-border bg-cyber-surface p-6 sticky top-24 self-start" data-testid="contact-detail-pane">
          {!active && <div className="font-mono-tech text-white/50">/ select a submission to inspect.</div>}
          {active && (
            <>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <TypePill type={active.type} />
                <a href={`mailto:${active.email}?subject=Re: your ${active.type} inquiry`} className="font-mono-tech text-xs text-white hover:text-cyber-green flex items-center gap-1" data-testid="reply-link">
                  <EnvelopeSimple size={12} /> Reply
                </a>
              </div>
              <h2 className="font-display font-black text-2xl">{active.name}</h2>
              <div className="font-mono-tech text-xs text-white/60 mt-1">{active.email}</div>
              <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
                <Info k="Company" v={active.company || "—"} />
                <Info k="Role" v={active.role || "—"} />
                <Info k="Submitted" v={new Date(active.created_at).toLocaleString()} />
                <Info k="Status" v={active.status} />
              </div>
              {active.type === "sponsorship" && (
                <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                  <Info k="Event interest" v={active.event_interest || "—"} />
                  <Info k="Budget" v={active.budget || "—"} />
                </div>
              )}
              <div className="mt-5">
                <div className="term-label mb-2">/ message</div>
                <div className="hard-border p-4 text-white/85 text-sm whitespace-pre-wrap leading-relaxed" data-testid="contact-message-body">{active.message}</div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button onClick={() => setStatus(active.id, "archived")} disabled={busyId === active.id} className="btn-ghost-neon flex-1 justify-center" data-testid="detail-archive-btn"><Archive size={12} weight="bold" /> Archive</button>
                <button onClick={() => remove(active.id)} className="font-mono-tech text-xs text-cyber-red border border-cyber-red px-4 py-3 uppercase tracking-[0.12em] hover:bg-cyber-red hover:text-white transition-colors" data-testid="detail-delete-btn">Delete</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TypePill = ({ type }) => (
  <span className={`font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${type === "sponsorship" ? "border-cyber-green text-cyber-green" : "border-white/40 text-white/70"}`}>
    {type}
  </span>
);

const Info = ({ k, v }) => (
  <div className="hard-border px-3 py-2">
    <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-[0.2em]">{k}</div>
    <div className="text-white text-sm break-words">{v}</div>
  </div>
);

export default AdminContacts;
